/**
 * App.js — Main application component
 *
 * Handles:
 *  - Theme selection (light/dark)
 *  - Loading spinner while auth/abilities initialize
 *  - Sidenav rendering for authenticated users
 *  - Route rendering from routes.js
 */

import { useState, useEffect, useContext } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import Sidenav from "components/Sidenav";

import theme from "assets/theme";
import themeDark from "assets/theme-dark";

import { AuthContext } from "context/AuthContext";
import { useAbilityLoading } from "context/AbilityContext";
import { PortalProvider } from "context/PortalContext";
import routes, { authRoutes } from "routes";
import Login from "layouts/authentication/Login";
import ResetPassword from "layouts/authentication/ResetPassword";
import PortalLayout from "layouts/portals/PortalLayout";

import { useMaterialUIController, setMiniSidenav } from "context";
import Configurator from "components/Configurator";
import LandingPage from "layouts/landing";

function App() {
  const { currentUser, activeRole, authLoading } = useContext(AuthContext);
  const { isReady: abilityReady } = useAbilityLoading();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    sidenavColor,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  // Scroll to top on navigation
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Sidenav hover expand/collapse
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Build <Route> elements from route array
  const getRoutes = (allRoutes) =>
    allRoutes.map((route, index) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key || `route-${index}`}
          />
        );
      }
      return null;
    });

  const getAuthRoutes = (allRoutes) =>
    allRoutes.map((route, index) => {
      if (route.route && route.routeRole === activeRole) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key || `auth-${index}`}
          />
        );
      }
      return null;
    });

  // Loading state — show spinner until auth + abilities are ready
  if (authLoading || (currentUser && !abilityReady)) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <KDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          sx={{
            background: darkMode
              ? "linear-gradient(195deg, #0F1114 0%, #161B1F 100%)"
              : "linear-gradient(195deg, #F8FAFC 0%, #F0FDFA 100%)",
          }}
        >
          <KDBox textAlign="center">
            <CircularProgress size={48} sx={{ color: "primary.main", mb: 2 }} />
            <KDTypography variant="h6" color={darkMode ? "white" : "dark"}>
              Loading...
            </KDTypography>
          </KDBox>
        </KDBox>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <PortalProvider>

      {/* Sidenav — only for authenticated users on non-public pages */}
      {currentUser && activeRole && !["/", "/login", "/reset-password"].includes(pathname) && (
        <Sidenav
          color={sidenavColor}
          brandName={activeRole === "admin" ? "Kadmin" : "Kadmin"}
          routes={routes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}

      {/* Configurator sidebar — settings panel */}
      {currentUser && activeRole && <Configurator />}

      {/* Routes */}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {getRoutes(routes)}
        {getAuthRoutes(authRoutes)}

        {/* Dynamic portal routes */}
        <Route path="/portal/:portalSlug" element={<PortalLayout />} />
        <Route path="/portal/:portalSlug/:pageSlug" element={<PortalLayout />} />

        {/* Catch-all redirect */}
        {!currentUser ? (
          <Route path="*" element={<Navigate to="/" replace />} />
        ) : (
          <Route path="*" element={<Navigate to={activeRole ? `/${activeRole}/dashboard` : "/login"} replace />} />
        )}
      </Routes>

      </PortalProvider>
    </ThemeProvider>
  );
}

export default App;
